import React, { useMemo, useState } from 'react';
import ConversationLogger from '../components/home/ConversationLogger';
import DailyChecklist from '../components/home/DailyChecklist';
import LogHistory from '../components/home/LogHistory';
import TemplatePicker from '../components/supplements/TemplatePicker';
import { Input } from '../components/ui/Input';
import { localToday } from '../utils/date';

// The logging hub. Two tabs:
//   • Log — an app-like fixed screen: the day's checklist sits up top and the conversation
//     fills the rest of the window, with the composer + mode bar docked at the bottom. The
//     page itself never scrolls; only the chat does. Everything you need to log stays put.
//   • History — the structured record of what you've logged, day by day (see LogHistory).
// Supplement quick-log (saved stacks) lives in a bottom sheet so it's one tap away without
// eating the conversation's window.

const TABS = [
  { key: 'log', label: 'Log' },
  { key: 'history', label: 'History' },
];

const LogPage = () => {
  const today = useMemo(() => localToday(), []);
  // The date lives here so the picker drives the checklist + composer on the Log tab.
  const [date, setDate] = useState(today);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState('log');
  const [sheetOpen, setSheetOpen] = useState(false);
  const bump = () => setRefreshKey((k) => k + 1);

  return (
    // One fixed-height column sized to the space between the header and the bottom nav, so
    // the Log tab snaps into place and the History tab scrolls within, not the document.
    // Layout drops the footer + bottom padding on /log, so we subtract only the fixed
    // header (56px clearance) and bottom nav (60px) on mobile; desktop has neither, leave a little.
    <div className="mx-auto flex w-full max-w-3xl flex-col px-4 sm:px-6 lg:px-8 h-[calc(100dvh-116px)] md:h-[calc(100dvh-2rem)]">
      {/* Header row: tabs on the left; on the Log tab, quick-log + date on the right. */}
      <div className="flex shrink-0 items-center justify-between gap-2 pt-1.5 pb-2">
        <div className="inline-flex rounded-lg border border-surface-border dark:border-surface-dark-border p-0.5 bg-gray-50 dark:bg-gray-900">
          {TABS.map((t) => {
            const selected = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`px-4 h-6 rounded-md text-sm font-medium transition-colors ${
                  selected
                    ? 'bg-primary-500 text-[#1a1200] shadow-sm'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'log' && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              aria-label="Log date"
              className="!w-auto h-8 text-sm shrink-0"
            />
          </div>
        )}
      </div>

      {tab === 'log' ? (
        <div className="flex min-h-0 flex-1 flex-col pb-3">
          <DailyChecklist
            refreshKey={refreshKey}
            defaultDate={date}
            onQuickLog={() => setSheetOpen(true)}
            onLogged={bump}
          />
          <div className="mt-3 flex min-h-0 flex-1 flex-col">
            <ConversationLogger fill defaultDate={date} onEntrySaved={bump} />
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pb-6">
          <LogHistory refreshKey={refreshKey} />
        </div>
      )}

      {/* Quick-log bottom sheet — the supplement stack picker, tucked away until asked for. */}
      {sheetOpen && (
        <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSheetOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-2xl border-t border-surface-border dark:border-surface-dark-border bg-surface dark:bg-surface-dark p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
            <div className="mb-3 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Close
              </button>
            </div>
            <TemplatePicker
              defaultDate={date}
              onEntrySaved={() => bump()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LogPage;
